import { useState, useEffect, useRef, useCallback } from 'react';
import styles from './AntennaLocationConfigEditor.module.scss';

function AntennaLocationConfigEditor({ configData, onConfigChange, isEditingJson }) {
  const [manualMode, setManualMode] = useState(false);
  const [auxXOffset, setAuxXOffset] = useState('0');
  const [auxYOffset, setAuxYOffset] = useState('0');
  const [auxZOffset, setAuxZOffset] = useState('0');

  const lastConfigDataRef = useRef('');
  const isUpdatingFromGuiRef = useRef(false);

  // Generate JSON from GUI settings
  const generateConfig = useCallback(() => {
    const config = {
      manual_mode: manualMode,
      aux_X_offset: parseFloat(auxXOffset) || 0,
      aux_Y_offset: parseFloat(auxYOffset) || 0,
      aux_Z_offset: parseFloat(auxZOffset) || 0,
    };

    return JSON.stringify(config, null, 2);
  }, [manualMode, auxXOffset, auxYOffset, auxZOffset]);

  // Parse JSON to GUI when configData changes from external source
  useEffect(() => {
    if (
      configData &&
      configData !== lastConfigDataRef.current &&
      !isUpdatingFromGuiRef.current &&
      !isEditingJson
    ) {
      lastConfigDataRef.current = configData;

      try {
        const config = JSON.parse(configData);

        setManualMode(config.manual_mode !== undefined ? config.manual_mode : false);
        setAuxXOffset(config.aux_X_offset !== undefined ? String(config.aux_X_offset) : '0');
        setAuxYOffset(config.aux_Y_offset !== undefined ? String(config.aux_Y_offset) : '0');
        setAuxZOffset(config.aux_Z_offset !== undefined ? String(config.aux_Z_offset) : '0');
      } catch (error) {
        console.error('Error parsing antenna location config:', error);
      }
    }
  }, [configData, isEditingJson]);

  // Update parent component when GUI settings change
  useEffect(() => {
    // Skip the first render and when we're parsing from external config
    if (lastConfigDataRef.current) {
      isUpdatingFromGuiRef.current = true;
      const newConfig = generateConfig();
      lastConfigDataRef.current = newConfig;
      onConfigChange(newConfig);

      // Reset the flag after a short delay to allow the parent to update
      setTimeout(() => {
        isUpdatingFromGuiRef.current = false;
      }, 10);
    }
  }, [manualMode, auxXOffset, auxYOffset, auxZOffset, generateConfig, onConfigChange]);

  const handleOffsetChange = (setter, value) => {
    // Allow decimal numbers (positive or negative)
    if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
      setter(value);
    }
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>Antenna Location Settings</h3>

      {/* Mode setting */}
      <div className={styles.setting}>
        <div className={styles.label}>Mode</div>
        <div className={styles.radioGroup}>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              value="auto"
              checked={!manualMode}
              onChange={() => setManualMode(false)}
            />
            Auto
          </label>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              value="manual"
              checked={manualMode}
              onChange={() => setManualMode(true)}
            />
            Manual
          </label>
        </div>
      </div>

      {/* Offset settings - only show when manual mode is selected */}
      {manualMode && (
        <div className={`${styles.setting} ${styles.settingWide}`}>
          <div className={styles.label}>Offset Settings</div>
          <div className={styles.fieldGrid}>
            <label className={styles.field}>
              <span className={styles.inputLabel}>AUX X Offset</span>
              <input
                type="text"
                value={auxXOffset}
                onChange={e => handleOffsetChange(setAuxXOffset, e.target.value)}
                className={styles.input}
                placeholder="0"
              />
            </label>
            <label className={styles.field}>
              <span className={styles.inputLabel}>AUX Y Offset</span>
              <input
                type="text"
                value={auxYOffset}
                onChange={e => handleOffsetChange(setAuxYOffset, e.target.value)}
                className={styles.input}
                placeholder="0"
              />
            </label>
            <label className={styles.field}>
              <span className={styles.inputLabel}>AUX Z Offset</span>
              <input
                type="text"
                value={auxZOffset}
                onChange={e => handleOffsetChange(setAuxZOffset, e.target.value)}
                className={styles.input}
                placeholder="0"
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

export default AntennaLocationConfigEditor;
